import React, { useState, useEffect } from "react";
import { Input, TimePicker, Tooltip, message } from "antd";
import { CalendarOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { SaveChangesBoxStyle } from "@/styles/StyledComponents/globalStyle";
import { CustomButton, SwitchButton } from "@/components";
import {
  useGetOperationsQuery,
  useAddOperationsMutation,
  useUpdateOperationsMutation,
} from "@/services/api/operation/operationApi";

const Hours = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    operatingHours: null,
    minimumNotice: "",
    downtime: false,
    maxConcurrentOrders: "",
    avgPrepTime: null,
  });

  // API hooks
  const { data: operationsData, isLoading, error, refetch } =
    useGetOperationsQuery();
  const [addOperations, { isLoading: isAdding }] = useAddOperationsMutation();
  const [updateOperations, { isLoading: isUpdating }] =
    useUpdateOperationsMutation();

  const operation = Array.isArray(operationsData) ? operationsData[0] : null;

  useEffect(() => {
    if (operation) {
      setFormData({
        operatingHours:
          operation.operating_hours_from && operation.operating_hours_to
            ? [
                dayjs(operation.operating_hours_from, "HH:mm:ss"),
                dayjs(operation.operating_hours_to, "HH:mm:ss"),
              ]
            : null,
        minimumNotice: operation.minimum_notice_period?.toString() || "",
        downtime: !!operation.down_time,
        maxConcurrentOrders: operation.max_concurrent_order?.toString() || "",
        avgPrepTime: operation.average_prep_time
          ? dayjs(operation.average_prep_time, "HH:mm:ss")
          : null,
      });
    }
  }, [operation]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberInput = (field, e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      handleInputChange(field, value);
    }
  };

  const handleDownTime = (checked) => {
    setFormData((prev) => ({
      ...prev,
      downtime: checked,
    }));
  };

  const toggleEditMode = async () => {
    if (isEditMode) {
      try {
        const payload = {
          operating_hours_from: formData.operatingHours
            ? formData.operatingHours[0].format("HH:mm:ss")
            : null,
          operating_hours_to: formData.operatingHours
            ? formData.operatingHours[1].format("HH:mm:ss")
            : null,
          minimum_notice_period: formData.minimumNotice
            ? parseInt(formData.minimumNotice, 10)
            : null,
          down_time: formData.downtime,
          max_concurrent_order: formData.maxConcurrentOrders
            ? parseInt(formData.maxConcurrentOrders, 10)
            : null,
          average_prep_time: formData.avgPrepTime
            ? formData.avgPrepTime.format("HH:mm:ss")
            : null,
        };

        if (operation?.id) {
          await updateOperations({ id: operation.id, requestData: payload }).unwrap();
          message.success("Operations updated successfully");
        } else {
          await addOperations(payload).unwrap();
          message.success("Operations created successfully");
        }

        refetch();
      } catch (error) {
        // console.error("Save error:", error);
        message.error(error?.data?.message || "Unable to save operations");
      }
    }
    setIsEditMode((prev) => !prev);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full p-8 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading operations data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full p-8 bg-white">
        <div className="text-center text-red-500">
          <p>Error loading operations data. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-8 bg-white">
      {/* Operating Hours */}
      <div className="mb-10 pb-8 border-b border-gray-200">
        <div className="flex items-start gap-16">
          <div className="w-80">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Operating Hours
            </h3>
            <p className="text-sm text-gray-500">
              Let us know your business hours
            </p>
          </div>
          <div className="flex-1">
            <TimePicker.RangePicker
              value={formData.operatingHours || []}
              onChange={(time) => handleInputChange("operatingHours", time)}
              format="HH:mm"
              placeholder={["Start time", "End time"]}
              className="w-full h-12"
              style={{ backgroundColor: "#f9fafb" }}
              disabled={!isEditMode}
            />
          </div>
        </div>
      </div>

      {/* Minimum Notice */}
      <div className="mb-10 pb-8 border-b border-gray-200">
        <div className="flex items-start gap-16">
          <div className="w-80">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Minimum Notice Period
            </h3>
            <p className="text-sm text-gray-500">
              Set up your minimum notice period (in minutes)
            </p>
          </div>
          <div className="flex-1">
            <Input
              value={formData.minimumNotice}
              onChange={(e) => handleNumberInput("minimumNotice", e)}
              placeholder="Enter minimum notice period (in days, e.g. 14 days)"
              className="w-full h-12 bg-gray-50 border-gray-200 rounded-lg text-gray-700"
              style={{ backgroundColor: "#f9fafb" }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              disabled={!isEditMode}
              suffix={<CalendarOutlined />}
            />
          </div>
        </div>
      </div>

      {/* Downtime */}
      <div className="mb-10 pb-8 border-b border-gray-200">
        <div className="flex items-center gap-16">
          <div className="w-80">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Downtime
            </h3>
            <p className="text-sm text-gray-500">
              Set your restaurant as unavailable during special days
            </p>
          </div>
          <div className="flex flex-1 items-center gap-4">
            <SwitchButton
              checked={formData?.downtime}
              onChange={(checked) => handleDownTime(checked)}
              disabled={!isEditMode}
            />
            <p className="mb-0">
              Store {formData?.downtime ? "Available" : "Unavailable"}
            </p>
          </div>
        </div>
      </div>

      {/* Kitchen Capacity */}
      <div>
        <div className="flex items-start gap-16">
          <div className="w-80">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Kitchen Capacity
            </h3>
            <p className="text-sm text-gray-500">
              Set up to determine estimated time of delivery
            </p>
          </div>
          <div className="flex-1 flex gap-6">
            <div className="flex-1">
              <div className="flex gap-1 items-center">
      <label className="block text-sm font-semibold text-gray-800 mb-2">
                Max Concurrent Orders (per day)
              </label>
              <Tooltip className="mb-1" title="Set the maximum number of concurrent orders your kitchen can handle per day. This helps manage workload and ensures timely preparation and delivery.">
                <InfoCircleOutlined/>
              </Tooltip>
                </div>
              <Input
                value={formData.maxConcurrentOrders}
                onChange={(e) => handleNumberInput("maxConcurrentOrders", e)}
                placeholder="Enter max concurrent orders"
                className="w-full h-12 bg-gray-50 border-gray-200 rounded-lg text-gray-700"
                style={{ backgroundColor: "#f9fafb" }}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                disabled={!isEditMode}
              />
            </div>
            <div className="flex-1">
              <div className="flex gap-1 items-center">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                Avg Prep Time (minutes)
              </label>
              <Tooltip className="mb-1" title="Set the average preparation time for orders. This helps in estimating delivery times and managing customer expectations.">
                <InfoCircleOutlined/>
              </Tooltip>
              </div>
            
              <TimePicker
                value={formData.avgPrepTime || null}
                onChange={(time) => handleInputChange("avgPrepTime", time)}
                format="HH:mm:ss"
                placeholder="Select prep time"
                className="w-full h-12"
                style={{ backgroundColor: "#f9fafb" }}
                showNow={false}
                minuteStep={1}
                disabled={!isEditMode}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={SaveChangesBoxStyle}>
        <CustomButton
          onClick={toggleEditMode}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          loading={isUpdating || isAdding}
          disabled={isLoading || error}
        >
          {isEditMode ? "Save Changes" : "Edit"}
        </CustomButton>
      </div>
    </div>
  );
};

export default Hours;
